import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";

import type { Quote } from "~/v2/api/rest/data";
import { Button } from "~/v2/components/common/Button";
import type { OrderType, Side, TrailType } from "~/v2/constants/equityOrder";
import { cn } from "~/v2/utils/cn";
import type { TrailingStopPrice } from "../hooks/useTrailingStopPrice";

interface EquityReviewCardProps {
  side: Side;
  symbol: string;
  quote: Quote | undefined;
  feedLabel: string;
  lastUpdated: string;
  onRefreshQuoteClicked: () => void;
  isRefreshing?: boolean;
  isQuoteLoading?: boolean;
  // Order details
  orderType: OrderType;
  quantity: number;
  limitPrice: number | undefined;
  stopPrice: number | undefined;
  trailType: TrailType;
  trailValue: number | string | undefined;
  trailingStopPrice: TrailingStopPrice;
  timeInForce: string;
  midPrice: string;
  // Financials
  estimatedCost: string;
  buyingPower: string;
  buyingPowerLabel: string;
  // Actions
  isSubmitLoading: boolean;
  isSubmitCompleted: boolean;
  onEditClicked: () => void;
  onSubmitClicked: () => void;
}

const showMidPrice = (orderType: OrderType) =>
  orderType === "market" || orderType === "limit" || orderType === "stop";

const getOrderTypeLabel = (orderType: OrderType): string => {
  switch (orderType) {
    case "stop_limit":
      return "Stop Limit";
    case "trailing_stop":
      return "Trailing Stop";
    default:
      return startCase(orderType);
  }
};

const formatPrice = (value: number | undefined): string => {
  if (value === undefined || Number.isNaN(value)) {
    return "-";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: "currency",
    currency: "USD",
  });
};

export const EquityReviewCard = ({
  side,
  symbol,
  quote,
  feedLabel,
  lastUpdated,
  onRefreshQuoteClicked,
  isRefreshing,
  isQuoteLoading,
  orderType,
  quantity,
  limitPrice,
  stopPrice,
  trailType,
  trailValue,
  trailingStopPrice,
  timeInForce,
  midPrice,
  estimatedCost,
  buyingPower,
  buyingPowerLabel,
  isSubmitLoading,
  isSubmitCompleted,
  onEditClicked,
  onSubmitClicked,
}: EquityReviewCardProps) => {
  const bid = quote?.bid?.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const ask = quote?.ask?.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const mid =
    quote?.bid != null && quote?.ask != null
      ? ((quote.bid + quote.ask) / 2).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : undefined;

  const primaryPrice = (() => {
    switch (orderType) {
      case "limit":
        return formatPrice(limitPrice);
      case "stop":
        return formatPrice(stopPrice);
      case "stop_limit":
        return formatPrice(limitPrice);
      case "trailing_stop":
        return trailingStopPrice[side];
      default:
        return midPrice;
    }
  })();

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <p className="m-0 p-0 font-semibold text-base text-gray-900">
        {startCase(side)} {symbol}
      </p>

      {/* Quote row */}
      {isQuoteLoading && !quote ? (
        <div className="flex flex-col gap-2">
          <div className="flex justify-between gap-0.5">
            <div className="flex flex-col gap-0.5">
              <div className="h-[14px] w-6 animate-pulse rounded bg-gray-200" />
              <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <div className="h-[14px] w-6 animate-pulse rounded bg-gray-200" />
              <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <div className="h-[14px] w-6 animate-pulse rounded bg-gray-200" />
              <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
            </div>
          </div>
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
        </div>
      ) : !quote ? (
        <div className="flex items-center justify-between">
          <p className="m-0 p-0 text-gray-500 text-sm">Quote unavailable</p>
          <button
            aria-label="Refresh quote"
            onClick={onRefreshQuoteClicked}
            type="button"
          >
            <ArrowsClockwiseIcon
              size={16}
              className="text-indigo-600 hover:text-indigo-500 active:text-indigo-400"
            />
          </button>
        </div>
      ) : (
        <>
          <div className="flex justify-between gap-0.5">
            <div className="flex flex-col items-start justify-start gap-0.5">
              <p className="m-0 p-0 text-[10px] text-gray-800 leading-[14px]">
                Bid
              </p>
              <p className="m-0 p-0 font-mono text-emerald-600 text-sm">
                {bid}
              </p>
            </div>
            {showMidPrice(orderType) && mid && (
              <>
                <div className="mt-3 h-[22px] w-px bg-gray-200" />
                <div className="flex flex-col items-center justify-start gap-0.5">
                  <p className="m-0 p-0 text-[10px] text-gray-800 leading-[14px]">
                    Mid
                  </p>
                  <p className="m-0 p-0 font-mono text-gray-900 text-sm">
                    {mid}
                  </p>
                </div>
              </>
            )}
            <div className="mt-3 h-[22px] w-px bg-gray-200" />
            <div className="flex flex-col items-end justify-start gap-0.5">
              <p className="m-0 p-0 text-[10px] text-gray-800 leading-[14px]">
                Ask
              </p>
              <p className="m-0 p-0 font-mono text-red-500 text-sm">{ask}</p>
            </div>
          </div>

          {/* Last updated + feed label */}
          <div className="flex items-center justify-between">
            <p className="m-0 p-0 text-gray-500 text-xs">
              {feedLabel && <span>{feedLabel}</span>}
            </p>
            <div className="flex items-center gap-1">
              <p className="m-0 p-0 font-mono text-gray-500 text-xs">
                {lastUpdated}
              </p>
              <button
                aria-label="Refresh quote"
                onClick={onRefreshQuoteClicked}
                disabled={isRefreshing}
                type="button"
              >
                <ArrowsClockwiseIcon
                  size={16}
                  className={cn(
                    "text-indigo-600 hover:text-indigo-500 active:text-indigo-400",
                    isRefreshing && "animate-spin",
                  )}
                />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Divider */}
      <hr className="m-0 border-gray-200" />

      {/* Order details */}
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <p className="m-0 p-0 text-gray-800 text-sm">Quantity</p>
          <p className="m-0 p-0 font-mono text-gray-900 text-sm">
            {quantity.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}
          </p>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-0.5">
            <p className="m-0 p-0 text-gray-800 text-sm">
              {getOrderTypeLabel(orderType)}
            </p>
            {orderType !== "market" && (
              <p className="m-0 p-0 text-gray-500 text-xs">Market Price</p>
            )}
            {orderType === "stop_limit" && (
              <p className="m-0 p-0 text-gray-500 text-xs">Stop</p>
            )}
            {orderType === "trailing_stop" && (
              <p className="m-0 p-0 text-gray-500 text-xs">
                {trailType === "trail_price" ? "Trail Price" : "Trail Percent"}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <p className="m-0 p-0 font-mono text-gray-900 text-sm">
              {primaryPrice}
            </p>
            {orderType !== "market" && (
              <p className="m-0 p-0 font-mono text-gray-500 text-xs">
                {midPrice}
              </p>
            )}
            {orderType === "stop_limit" && (
              <p className="m-0 p-0 font-mono text-gray-500 text-xs">
                {formatPrice(stopPrice)}
              </p>
            )}
            {orderType === "trailing_stop" && (
              <p className="m-0 p-0 font-mono text-gray-500 text-xs">
                {trailType === "trail_price"
                  ? `$${trailValue}`
                  : `${trailValue}%`}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="m-0 p-0 text-gray-800 text-sm">Time in Force</p>
          <p className="m-0 p-0 text-gray-900 text-sm">
            {timeInForce.toUpperCase()}
          </p>
        </div>
      </div>

      {/* Divider */}
      <hr className="m-0 border-gray-200" />

      {/* Financials */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <p className="m-0 p-0 text-gray-700 text-sm">{buyingPowerLabel}</p>
          <p className="m-0 p-0 font-mono text-gray-800 text-sm">
            {buyingPower}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="m-0 p-0 font-medium text-gray-900 text-sm">
            Estimated Total
          </p>
          <p className="m-0 p-0 font-medium font-mono text-gray-900 text-sm">
            {estimatedCost}
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button
          variant="alternate"
          onClick={onEditClicked}
          type="button"
          disabled={isSubmitLoading}
          className="h-9 rounded text-sm"
        >
          Edit
        </Button>
        <Button
          onClick={onSubmitClicked}
          type="button"
          disabled={isSubmitCompleted}
          loading={isSubmitLoading}
          loadingPlaceholder="Submitting..."
          className="h-9 rounded text-sm"
        >
          Submit Order
        </Button>
      </div>
    </div>
  );
};
